import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import type { Navigation } from '../types/content'
import ThemeToggle from './ThemeToggle'

interface HeaderProps {
  data: Navigation
}

export default function Header({ data }: HeaderProps) {
  const [isOpen, setIsOpen] = useState(false)

  const navLinks = data.links.filter(link => !link.isButton)
  const navButton = data.links.find(link => link.isButton)

  return (
    <header className="fixed top-0 left-0 right-0 h-20 bg-dark/90 backdrop-blur-md border-b border-border z-50">
      <div className="container mx-auto px-6 h-full flex items-center justify-between">
        <a href="#hero" className="flex items-center gap-3 group">
          <img
            src={data.brand.logo}
            alt={data.brand.name}
            className="h-10 w-auto transition-transform group-hover:scale-105"
          />
          <span
            className="text-xl font-bold text-gradient tracking-wide"
            style={{ fontFamily: "'Exo 2', sans-serif" }}
          >
            {data.brand.name}
          </span>
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map(link => (
            <a
              key={link.id}
              href={link.href}
              className="text-sm text-gray-400 hover:text-white transition-colors relative group"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-primary transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
          <Link
            to="/personal-data"
            className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
            title="Персональные данные"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </Link>
          <ThemeToggle />
          {navButton && (
            <a
              href={navButton.href}
              className="px-4 py-2 bg-gradient-primary text-dark text-sm font-medium rounded-lg hover:shadow-glow transition-all"
            >
              {navButton.label}
            </a>
          )}
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden flex flex-col gap-1.5 p-2"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
        >
          <motion.span
            className="block w-6 h-0.5 bg-primary"
            animate={isOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
          />
          <motion.span
            className="block w-6 h-0.5 bg-primary-light"
            animate={isOpen ? { opacity: 0 } : { opacity: 1 }}
          />
          <motion.span
            className="block w-6 h-0.5 bg-secondary"
            animate={isOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }}
          />
        </button>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.nav
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden absolute top-20 left-0 right-0 bg-dark border-b border-border p-6 flex flex-col gap-4"
          >
            {navLinks.map(link => (
              <a
                key={link.id}
                href={link.href}
                className="text-lg text-gray-400 hover:text-white transition-colors"
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </a>
            ))}
            {navButton && (
              <a
                href={navButton.href}
                className="px-4 py-2 bg-gradient-primary text-dark text-center font-medium rounded-lg"
                onClick={() => setIsOpen(false)}
              >
                {navButton.label}
              </a>
            )}
            <div className="flex items-center gap-4 pt-2 border-t border-border">
              <ThemeToggle />
              <Link
                to="/personal-data"
                className="text-sm text-gray-500 hover:text-gray-300 transition-colors flex items-center gap-2"
                onClick={() => setIsOpen(false)}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Персональные данные
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  )
}
